import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, LogOut, Package, Users, DollarSign, ShoppingBag, Boxes, RefreshCw, CheckCircle2, Save, Tag, Plus, Trash2, Award } from "lucide-react";
import { useAuth, formatApiError } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { id: "kpis", label: "Overview", icon: DollarSign },
  { id: "orders", label: "Orders", icon: ShoppingBag },
  { id: "inquiries", label: "Wholesale", icon: Users },
  { id: "products", label: "Products", icon: Boxes },
  { id: "coupons", label: "Coupons", icon: Tag },
  { id: "users", label: "Customers", icon: Users },
  { id: "affiliates", label: "Affiliates", icon: Award },
];

export default function AdminDashboard() {
  const { admin, adminLogout, adminHeader } = useAuth();
  const [tab, setTab] = useState("kpis");

  return (
    <div data-testid="admin-dashboard-page" className="bg-[#050505] text-white pt-32 min-h-screen">
      <div className="px-6 md:px-12 lg:px-24 pb-24 max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-start justify-between mb-12">
          <div>
            <p className="eyebrow mb-3">Admin Console</p>
            <h1 className="font-display text-4xl sm:text-5xl font-medium tracking-tight">
              Stageletics HQ
            </h1>
            <p className="text-zinc-400 mt-3 text-sm">Signed in as {admin?.email}</p>
          </div>
          <button
            data-testid="admin-logout-button"
            onClick={adminLogout}
            className="btn-secondary !text-zinc-300 !border-white/20"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 border-b border-white/10 mb-10">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                data-testid={`admin-tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`px-5 py-3 text-sm uppercase tracking-[0.15em] flex items-center gap-2 border-b-2 transition-colors ${
                  active ? "border-[#E8A851] text-white" : "border-transparent text-zinc-500 hover:text-white"
                }`}
              >
                <Icon className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </div>

        {tab === "kpis" && <KpisPanel headers={adminHeader} />}
        {tab === "orders" && <OrdersPanel headers={adminHeader} />}
        {tab === "inquiries" && <InquiriesPanel headers={adminHeader} />}
        {tab === "products" && <ProductsPanel headers={adminHeader} />}
        {tab === "coupons" && <CouponsPanel headers={adminHeader} />}
        {tab === "users" && <UsersPanel headers={adminHeader} />}
        {tab === "affiliates" && <AffiliatesPanel headers={adminHeader} />}
      </div>
    </div>
  );
}

/* ---------- KPI Panel ---------- */
function KpisPanel({ headers }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [salesDigest, setSalesDigest] = useState(null);
  const [salesLoading, setSalesLoading] = useState(false);
  const [salesSending, setSalesSending] = useState(false);
  const [abandoned, setAbandoned] = useState(null);
  const [abandonedLoading, setAbandonedLoading] = useState(false);
  const [abandonedSending, setAbandonedSending] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/kpis`, { headers: headers() });
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const previewSalesDigest = async () => {
    setSalesLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/sales/digest/preview`, { headers: headers() });
      setSalesDigest(data);
    } catch (e) { toast.error(formatApiError(e)); }
    setSalesLoading(false);
  };

  const sendSalesDigest = async () => {
    setSalesSending(true);
    try {
      const { data } = await axios.post(`${API}/admin/sales/digest/send`, {}, { headers: headers() });
      toast.success(`Sales digest sent — ${data.digest.kpis.orders_this} orders, $${data.digest.kpis.revenue_this.toFixed(2)}`);
    } catch (e) { toast.error(formatApiError(e)); }
    setSalesSending(false);
  };

  const loadAbandoned = async () => {
    setAbandonedLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/abandoned/preview`, { headers: headers() });
      setAbandoned(data);
    } catch (e) { toast.error(formatApiError(e)); }
    setAbandonedLoading(false);
  };

  const sendAbandoned = async () => {
    setAbandonedSending(true);
    try {
      const { data } = await axios.post(`${API}/admin/abandoned/send`, {}, { headers: headers() });
      toast.success(
        data.candidates === 0
          ? "No abandoned carts to nudge right now"
          : `Sent ${data.sent} nudge${data.sent === 1 ? "" : "s"} (${data.skipped} skipped)`
      );
      // Refresh preview
      setAbandoned([]);
    } catch (e) { toast.error(formatApiError(e)); }
    setAbandonedSending(false);
  };

  if (loading) return <PanelLoading />;
  if (!data) return <p className="text-zinc-500">No data.</p>;

  const Card = ({ label, value, sub, testid }) => (
    <div data-testid={testid} className="border border-white/10 bg-white/[0.02] p-6">
      <p className="text-xs uppercase tracking-[0.18em] text-zinc-500 mb-2">{label}</p>
      <p className="font-display text-3xl font-semibold">{value}</p>
      {sub && <p className="text-xs text-zinc-500 mt-2">{sub}</p>}
    </div>
  );

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card testid="kpi-today"
          label="Today" value={`$${data.today.revenue.toFixed(2)}`}
          sub={`${data.today.orders} orders`} />
        <Card testid="kpi-week"
          label="Last 7 days" value={`$${data.week.revenue.toFixed(2)}`}
          sub={`${data.week.orders} orders`} />
        <Card testid="kpi-month"
          label="Last 30 days" value={`$${data.month.revenue.toFixed(2)}`}
          sub={`${data.month.orders} orders`} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card testid="kpi-inquiries"
          label="Open wholesale inquiries" value={data.open_wholesale_inquiries}
          sub="Awaiting your response" />
        <div data-testid="kpi-low-stock" className="border border-white/10 bg-white/[0.02] p-6">
          <p className="text-xs uppercase tracking-[0.18em] text-zinc-500 mb-3">Low stock (≤20)</p>
          {data.low_stock.length === 0 ? (
            <p className="text-sm text-zinc-500">All inventory healthy.</p>
          ) : (
            <ul className="space-y-2 text-sm">
              {data.low_stock.map((p) => (
                <li key={p.id} className="flex justify-between">
                  <span>{p.name}</span>
                  <span className="text-[#E8A851]">{p.inventory} left</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Weekly sales snapshot */}
      <div data-testid="admin-sales-digest-toolbar" className="border border-white/10 bg-white/[0.02] p-5 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <p className="eyebrow mb-1">Weekly sales snapshot</p>
          <p className="text-xs text-zinc-500">
            Auto-emails every Monday 09:00 UTC. Orders + revenue vs. previous week, top SKUs, low-stock, new customers, inquiries.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="admin-sales-digest-preview-button"
            onClick={previewSalesDigest}
            disabled={salesLoading}
            className="btn-secondary !border-white/20 disabled:opacity-50"
          >
            {salesLoading ? <><Loader2 className="w-4 h-4 animate-spin" /> Loading…</> : <>Preview</>}
          </button>
          <button
            data-testid="admin-sales-digest-send-button"
            onClick={sendSalesDigest}
            disabled={salesSending}
            className="btn-secondary !border-[#E8A851]/40 !text-[#E8A851] disabled:opacity-50"
          >
            {salesSending ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <>Send now</>}
          </button>
        </div>
      </div>

      {salesDigest && (
        <div data-testid="admin-sales-digest-preview" className="border border-[#E8A851]/30 bg-[#E8A851]/[0.03] p-6 space-y-4">
          <p className="eyebrow">{salesDigest.period_label}</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div><span className="text-zinc-500">Orders:</span> <span className="text-white">{salesDigest.kpis.orders_this}</span> <span className="text-zinc-500 text-xs">(prev {salesDigest.kpis.orders_last})</span></div>
            <div><span className="text-zinc-500">Revenue:</span> <span className="text-[#E8A851]">${salesDigest.kpis.revenue_this.toFixed(2)}</span></div>
            <div><span className="text-zinc-500">AOV:</span> <span className="text-white">${salesDigest.kpis.aov.toFixed(2)}</span></div>
            <div><span className="text-zinc-500">New customers:</span> <span className="text-white">{salesDigest.new_customers}</span></div>
          </div>
          {salesDigest.top_skus.length > 0 && (
            <div>
              <p className="text-xs uppercase tracking-[0.15em] text-zinc-500 mb-2">Top SKUs</p>
              <div className="divide-y divide-white/10">
                {salesDigest.top_skus.map((s, i) => (
                  <div key={s.name || i} className="py-2 flex justify-between text-sm">
                    <span>{s.name}</span>
                    <span className="text-zinc-400">{s.units} units · ${s.revenue.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Abandoned cart recovery */}
      <div data-testid="admin-abandoned-toolbar" className="border border-white/10 bg-white/[0.02] p-5 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <p className="eyebrow mb-1">Abandoned cart recovery</p>
          <p className="text-xs text-zinc-500">
            Auto-nudges every 3 hours. Emails customers who left a cart 24h+ ago without paying. One nudge per session.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="admin-abandoned-preview-button"
            onClick={loadAbandoned}
            disabled={abandonedLoading}
            className="btn-secondary !border-white/20 disabled:opacity-50"
          >
            {abandonedLoading ? <><Loader2 className="w-4 h-4 animate-spin" /> Loading…</> : <>Preview</>}
          </button>
          <button
            data-testid="admin-abandoned-send-button"
            onClick={sendAbandoned}
            disabled={abandonedSending}
            className="btn-secondary !border-[#E8A851]/40 !text-[#E8A851] disabled:opacity-50"
          >
            {abandonedSending ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <>Send nudges now</>}
          </button>
        </div>
      </div>

      {abandoned !== null && (
        <div data-testid="admin-abandoned-preview" className="border border-[#E8A851]/30 bg-[#E8A851]/[0.03] p-5">
          {abandoned.length === 0 ? (
            <p className="text-sm text-zinc-400">No abandoned carts eligible right now.</p>
          ) : (
            <div className="divide-y divide-white/10">
              {abandoned.map((c) => (
                <div key={c.session_id} className="py-3 flex flex-wrap justify-between items-center gap-2 text-sm">
                  <div>
                    <span className="text-white">{c.customer_email}</span>
                    <span className="text-zinc-500 ml-3 text-xs">{new Date(c.created_at).toLocaleString()}</span>
                  </div>
                  <div className="text-zinc-400 text-xs">
                    {(c.items || []).map((it, i) => `${it.quantity}× ${it.sku}`).join(" · ")}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <button onClick={load} className="btn-secondary" data-testid="kpi-refresh">
        <RefreshCw className="w-4 h-4" /> Refresh
      </button>
    </div>
  );
}

/* ---------- Orders Panel ---------- */
function OrdersPanel({ headers }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/orders`, { headers: headers() });
      setOrders(data);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const fulfill = async (id) => {
    try {
      await axios.patch(`${API}/admin/orders/${id}/fulfill`, {}, { headers: headers() });
      toast.success("Order marked fulfilled");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (loading) return <PanelLoading />;
  if (orders.length === 0) return <EmptyPanel icon={ShoppingBag} label="No paid orders yet." />;

  return (
    <div className="border border-white/10 bg-white/[0.02] divide-y divide-white/10">
      {orders.map((o) => (
        <div key={o.id} data-testid={`admin-order-row-${o.id}`} className="p-5 grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
          <div className="md:col-span-3">
            <p className="text-xs text-zinc-500">{new Date(o.created_at).toLocaleString()}</p>
            <p className="text-sm text-white mt-1">{o.customer_email || "—"}</p>
          </div>
          <div className="md:col-span-4 text-sm text-zinc-300">
            {(o.items || []).map((it, i) => (
              <div key={`${it.sku}-${i}`}>{it.quantity}× <span className="text-zinc-500">{it.sku}</span></div>
            ))}
          </div>
          <div className="md:col-span-2 text-sm">
            <p>Subtotal: <span className="text-white">${Number(o.subtotal||0).toFixed(2)}</span></p>
            <p className="text-zinc-500">+ ship ${Number(o.shipping||0).toFixed(2)} · tax ${Number(o.tax||0).toFixed(2)}</p>
          </div>
          <div className="md:col-span-1 text-right font-display text-lg">${Number(o.amount_total||0).toFixed(2)}</div>
          <div className="md:col-span-2 text-right">
            {o.fulfillment_status === "fulfilled" ? (
              <span className="inline-flex items-center gap-1 text-xs text-emerald-400 uppercase tracking-[0.15em]">
                <CheckCircle2 className="w-3 h-3" /> Fulfilled
              </span>
            ) : (
              <button
                onClick={() => fulfill(o.id)}
                data-testid={`admin-fulfill-${o.id}`}
                className="text-xs uppercase tracking-[0.15em] border border-[#E8A851]/40 text-[#E8A851] hover:bg-[#E8A851]/10 px-3 py-2"
              >
                Mark fulfilled
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ---------- Inquiries Panel ---------- */
function InquiriesPanel({ headers }) {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/wholesale-inquiries`, { headers: headers() });
      setInquiries(data);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const setStatus = async (id, status) => {
    try {
      await axios.patch(
        `${API}/admin/wholesale-inquiries/${id}/status?status=${status}`,
        {},
        { headers: headers() }
      );
      toast.success(`Marked ${status}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (loading) return <PanelLoading />;
  if (inquiries.length === 0) return <EmptyPanel icon={Users} label="No wholesale inquiries yet." />;

  return (
    <div className="border border-white/10 bg-white/[0.02] divide-y divide-white/10">
      {inquiries.map((q) => (
        <div key={q.id} data-testid={`admin-inquiry-row-${q.id}`} className="p-5 space-y-3">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="font-display text-lg">{q.organization}</p>
              <p className="text-sm text-zinc-400">{q.name} · {q.email}{q.role ? ` · ${q.role}` : ""}</p>
              <p className="text-xs text-zinc-500 mt-1">
                {q.quantity} units{q.timeline ? ` · ${q.timeline}` : ""} · {new Date(q.created_at).toLocaleDateString()}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <StatusPill status={q.status} />
              <select
                data-testid={`admin-inquiry-status-${q.id}`}
                value={q.status}
                onChange={(e) => setStatus(q.id, e.target.value)}
                className="bg-black/40 border border-white/10 text-xs uppercase tracking-[0.12em] px-3 py-2"
              >
                <option value="new">New</option>
                <option value="contacted">Contacted</option>
                <option value="closed">Closed</option>
              </select>
            </div>
          </div>
          {q.message && <p className="text-sm text-zinc-300 border-l-2 border-[#E8A851]/30 pl-4">{q.message}</p>}
        </div>
      ))}
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    new: "border-[#E8A851]/40 text-[#E8A851]",
    contacted: "border-blue-400/40 text-blue-300",
    closed: "border-white/20 text-zinc-400",
  };
  return (
    <span className={`text-[10px] uppercase tracking-[0.15em] border rounded-full px-2 py-1 ${map[status] || map.new}`}>
      {status || "new"}
    </span>
  );
}

/* ---------- Products Panel ---------- */
function ProductsPanel({ headers }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [showNew, setShowNew] = useState(false);
  const [newP, setNewP] = useState({ id: "", name: "", description: "", unit_price: "", inventory: 0, active: true });
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/products`, { headers: headers() });
      setProducts(data);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const setField = (sku, field, value) =>
    setProducts((prev) => prev.map((p) => (p.id === sku ? { ...p, [field]: value } : p)));

  const save = async (p) => {
    setSaving((s) => ({ ...s, [p.id]: true }));
    try {
      await axios.patch(
        `${API}/admin/products/${p.id}`,
        {
          name: p.name,
          description: p.description,
          unit_price: parseFloat(p.unit_price),
          inventory: parseInt(p.inventory, 10),
          active: !!p.active,
        },
        { headers: headers() }
      );
      toast.success("Saved");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setSaving((s) => ({ ...s, [p.id]: false }));
  };

  const del = async (id) => {
    if (!window.confirm(`Delete product "${id}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/products/${id}`, { headers: headers() });
      toast.success("Product deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const createNew = async () => {
    if (!newP.id || !newP.name || newP.unit_price === "") {
      toast.error("id, name, and unit_price are required");
      return;
    }
    setCreating(true);
    try {
      await axios.post(
        `${API}/admin/products`,
        {
          id: newP.id.toLowerCase().trim(),
          name: newP.name.trim(),
          description: newP.description || "",
          unit_price: parseFloat(newP.unit_price),
          inventory: parseInt(newP.inventory || 0, 10),
          active: !!newP.active,
        },
        { headers: headers() }
      );
      toast.success("Product created");
      setShowNew(false);
      setNewP({ id: "", name: "", description: "", unit_price: "", inventory: 0, active: true });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setCreating(false);
  };

  if (loading) return <PanelLoading />;

  return (
    <div className="space-y-6">
      {/* Add-product bar */}
      <div className="flex justify-between items-center">
        <p className="text-sm text-zinc-500">{products.length} products in catalog</p>
        <button
          data-testid="admin-new-product-toggle"
          onClick={() => setShowNew((v) => !v)}
          className="btn-secondary !border-[#E8A851]/40 !text-[#E8A851]"
        >
          <Plus className="w-4 h-4" /> {showNew ? "Cancel" : "New product"}
        </button>
      </div>

      {showNew && (
        <div data-testid="admin-new-product-form" className="border border-[#E8A851]/40 bg-[#E8A851]/[0.03] p-6">
          <p className="eyebrow mb-4">New product</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="eyebrow mb-2 block">ID (kebab-case, unique)</label>
              <input
                data-testid="admin-new-product-id"
                value={newP.id}
                onChange={(e) => setNewP({ ...newP, id: e.target.value })}
                placeholder="aura-bundle-6"
                className="w-full bg-transparent border-b border-white/20 focus:border-[#E8A851] text-white px-0 py-2 focus:outline-none"
              />
            </div>
            <div>
              <label className="eyebrow mb-2 block">Name</label>
              <input
                data-testid="admin-new-product-name"
                value={newP.name}
                onChange={(e) => setNewP({ ...newP, name: e.target.value })}
                placeholder="Aura Sleeve — Bundle of 6"
                className="w-full bg-transparent border-b border-white/20 focus:border-[#E8A851] text-white px-0 py-2 focus:outline-none"
              />
            </div>
            <div className="md:col-span-2">
              <label className="eyebrow mb-2 block">Description</label>
              <textarea
                data-testid="admin-new-product-desc"
                rows={2}
                value={newP.description}
                onChange={(e) => setNewP({ ...newP, description: e.target.value })}
                className="w-full bg-transparent border-b border-white/20 focus:border-[#E8A851] text-white px-0 py-2 focus:outline-none resize-none"
              />
            </div>
            <div>
              <label className="eyebrow mb-2 block">Unit price (USD)</label>
              <input
                data-testid="admin-new-product-price"
                type="number" step="0.01" min="0"
                value={newP.unit_price}
                onChange={(e) => setNewP({ ...newP, unit_price: e.target.value })}
                className="w-full bg-transparent border-b border-white/20 focus:border-[#E8A851] text-white px-0 py-2 focus:outline-none"
              />
            </div>
            <div>
              <label className="eyebrow mb-2 block">Inventory</label>
              <input
                data-testid="admin-new-product-inventory"
                type="number" min="0" step="1"
                value={newP.inventory}
                onChange={(e) => setNewP({ ...newP, inventory: e.target.value })}
                className="w-full bg-transparent border-b border-white/20 focus:border-[#E8A851] text-white px-0 py-2 focus:outline-none"
              />
            </div>
          </div>
          <button
            data-testid="admin-new-product-create"
            disabled={creating}
            onClick={createNew}
            className="btn-primary mt-6 disabled:opacity-50"
          >
            {creating ? <><Loader2 className="w-4 h-4 animate-spin" /> Creating…</> : <><Plus className="w-4 h-4" /> Create product</>}
          </button>
        </div>
      )}

      {products.map((p) => (
        <div key={p.id} data-testid={`admin-product-${p.id}`} className="border border-white/10 bg-white/[0.02] p-6">
          <div className="flex justify-between items-start mb-4">
            <p className="eyebrow">{p.id}</p>
            <button
              data-testid={`admin-product-delete-${p.id}`}
              onClick={() => del(p.id)}
              className="text-xs text-red-400 hover:text-red-300 uppercase tracking-[0.15em] flex items-center gap-1"
            >
              <Trash2 className="w-3 h-3" /> Delete
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="eyebrow mb-2 block">Name</label>
              <input
                data-testid={`admin-product-name-${p.id}`}
                value={p.name}
                onChange={(e) => setField(p.id, "name", e.target.value)}
                className="w-full bg-transparent border-b border-white/20 focus:border-[#E8A851] text-white px-0 py-2 focus:outline-none"
              />
            </div>
            <div>
              <label className="eyebrow mb-2 block">Unit price (USD)</label>
              <input
                data-testid={`admin-product-price-${p.id}`}
                type="number" step="0.01" min="0"
                value={p.unit_price}
                onChange={(e) => setField(p.id, "unit_price", e.target.value)}
                className="w-full bg-transparent border-b border-white/20 focus:border-[#E8A851] text-white px-0 py-2 focus:outline-none"
              />
            </div>
            <div className="md:col-span-2">
              <label className="eyebrow mb-2 block">Description</label>
              <textarea
                data-testid={`admin-product-desc-${p.id}`}
                value={p.description || ""}
                onChange={(e) => setField(p.id, "description", e.target.value)}
                rows={2}
                className="w-full bg-transparent border-b border-white/20 focus:border-[#E8A851] text-white px-0 py-2 focus:outline-none resize-none"
              />
            </div>
            <div>
              <label className="eyebrow mb-2 block">Inventory (units in stock)</label>
              <input
                data-testid={`admin-product-inventory-${p.id}`}
                type="number" min="0" step="1"
                value={p.inventory}
                onChange={(e) => setField(p.id, "inventory", e.target.value)}
                className="w-full bg-transparent border-b border-white/20 focus:border-[#E8A851] text-white px-0 py-2 focus:outline-none"
              />
            </div>
            <div className="flex items-center gap-3">
              <input
                data-testid={`admin-product-active-${p.id}`}
                type="checkbox"
                id={`active-${p.id}`}
                checked={!!p.active}
                onChange={(e) => setField(p.id, "active", e.target.checked)}
              />
              <label htmlFor={`active-${p.id}`} className="text-sm text-zinc-300">Active (visible to shoppers)</label>
            </div>
          </div>
          <button
            onClick={() => save(p)}
            disabled={!!saving[p.id]}
            data-testid={`admin-product-save-${p.id}`}
            className="btn-primary mt-6 disabled:opacity-50"
          >
            {saving[p.id] ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : <><Save className="w-4 h-4" /> Save changes</>}
          </button>
        </div>
      ))}
    </div>
  );
}

/* ---------- Coupons Panel ---------- */
function CouponsPanel({ headers }) {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [newC, setNewC] = useState({
    code: "", kind: "percent", value: "", min_subtotal: "", max_uses: "", expires_at: "", active: true,
  });
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/coupons`, { headers: headers() });
      setCoupons(data);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const create = async () => {
    if (!newC.code || newC.value === "") { toast.error("code and value are required"); return; }
    setCreating(true);
    try {
      const body = {
        code: newC.code.trim().toUpperCase(),
        kind: newC.kind,
        value: parseFloat(newC.value),
        active: !!newC.active,
      };
      if (newC.min_subtotal !== "") body.min_subtotal = parseFloat(newC.min_subtotal);
      if (newC.max_uses !== "") body.max_uses = parseInt(newC.max_uses, 10);
      if (newC.expires_at) body.expires_at = new Date(newC.expires_at).toISOString();
      await axios.post(`${API}/admin/coupons`, body, { headers: headers() });
      toast.success("Coupon created");
      setShowNew(false);
      setNewC({ code: "", kind: "percent", value: "", min_subtotal: "", max_uses: "", expires_at: "", active: true });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setCreating(false);
  };

  const toggleActive = async (c) => {
    try {
      await axios.patch(`${API}/admin/coupons/${c.code}`, { active: !c.active }, { headers: headers() });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (code) => {
    if (!window.confirm(`Delete coupon ${code}?`)) return;
    try {
      await axios.delete(`${API}/admin/coupons/${code}`, { headers: headers() });
      toast.success("Coupon deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (loading) return <PanelLoading />;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <p className="text-sm text-zinc-500">{coupons.length} coupons</p>
        <button
          data-testid="admin-new-coupon-toggle"
          onClick={() => setShowNew((v) => !v)}
          className="btn-secondary !border-[#E8A851]/40 !text-[#E8A851]"
        >
          <Plus className="w-4 h-4" /> {showNew ? "Cancel" : "New coupon"}
        </button>
      </div>

      {showNew && (
        <div data-testid="admin-new-coupon-form" className="border border-[#E8A851]/40 bg-[#E8A851]/[0.03] p-6">
          <p className="eyebrow mb-4">New coupon</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="eyebrow mb-2 block">Code</label>
              <input
                data-testid="admin-new-coupon-code"
                value={newC.code}
                onChange={(e) => setNewC({ ...newC, code: e.target.value.toUpperCase() })}
                placeholder="WORSHIP15"
                className="w-full bg-transparent border-b border-white/20 focus:border-[#E8A851] text-white px-0 py-2 focus:outline-none uppercase tracking-wider"
              />
            </div>
            <div>
              <label className="eyebrow mb-2 block">Kind</label>
              <select
                data-testid="admin-new-coupon-kind"
                value={newC.kind}
                onChange={(e) => setNewC({ ...newC, kind: e.target.value })}
                className="w-full bg-transparent border-b border-white/20 focus:border-[#E8A851] text-white px-0 py-2 focus:outline-none"
              >
                <option value="percent">Percent off</option>
                <option value="amount">Fixed $ off</option>
              </select>
            </div>
            <div>
              <label className="eyebrow mb-2 block">
                Value {newC.kind === "percent" ? "(0-100)" : "(USD)"}
              </label>
              <input
                data-testid="admin-new-coupon-value"
                type="number" step={newC.kind === "percent" ? "1" : "0.01"} min="0"
                max={newC.kind === "percent" ? 100 : undefined}
                value={newC.value}
                onChange={(e) => setNewC({ ...newC, value: e.target.value })}
                className="w-full bg-transparent border-b border-white/20 focus:border-[#E8A851] text-white px-0 py-2 focus:outline-none"
              />
            </div>
            <div>
              <label className="eyebrow mb-2 block">Min subtotal (optional)</label>
              <input
                data-testid="admin-new-coupon-min"
                type="number" step="0.01" min="0"
                value={newC.min_subtotal}
                onChange={(e) => setNewC({ ...newC, min_subtotal: e.target.value })}
                className="w-full bg-transparent border-b border-white/20 focus:border-[#E8A851] text-white px-0 py-2 focus:outline-none"
              />
            </div>
            <div>
              <label className="eyebrow mb-2 block">Max uses (optional)</label>
              <input
                data-testid="admin-new-coupon-max-uses"
                type="number" min="1" step="1"
                value={newC.max_uses}
                onChange={(e) => setNewC({ ...newC, max_uses: e.target.value })}
                className="w-full bg-transparent border-b border-white/20 focus:border-[#E8A851] text-white px-0 py-2 focus:outline-none"
              />
            </div>
            <div>
              <label className="eyebrow mb-2 block">Expires (optional)</label>
              <input
                data-testid="admin-new-coupon-expires"
                type="datetime-local"
                value={newC.expires_at}
                onChange={(e) => setNewC({ ...newC, expires_at: e.target.value })}
                className="w-full bg-transparent border-b border-white/20 focus:border-[#E8A851] text-white px-0 py-2 focus:outline-none"
              />
            </div>
          </div>
          <button
            data-testid="admin-new-coupon-create"
            disabled={creating}
            onClick={create}
            className="btn-primary mt-6 disabled:opacity-50"
          >
            {creating ? <><Loader2 className="w-4 h-4 animate-spin" /> Creating…</> : <><Plus className="w-4 h-4" /> Create coupon</>}
          </button>
        </div>
      )}

      {coupons.length === 0 ? (
        <EmptyPanel icon={Tag} label="No coupons yet — create one to start running promos." />
      ) : (
        <div className="border border-white/10 bg-white/[0.02] divide-y divide-white/10">
          {coupons.map((c) => (
            <div key={c.code} data-testid={`admin-coupon-row-${c.code}`} className="p-5 grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
              <div className="md:col-span-3">
                <p className="font-mono text-lg text-[#E8A851]">{c.code}</p>
                <p className="text-xs text-zinc-500 mt-1">Created {new Date(c.created_at).toLocaleDateString()}</p>
              </div>
              <div className="md:col-span-3 text-sm">
                {c.kind === "percent" ? `${c.value}% off` : `$${Number(c.value).toFixed(2)} off`}
                {c.min_subtotal ? <span className="text-zinc-500"> · min ${Number(c.min_subtotal).toFixed(2)}</span> : null}
              </div>
              <div className="md:col-span-3 text-sm text-zinc-400">
                Uses: <span className="text-white">{c.uses || 0}</span>
                {c.max_uses ? <span className="text-zinc-500"> / {c.max_uses}</span> : null}
                {c.expires_at && <div className="text-xs mt-1">Expires {new Date(c.expires_at).toLocaleDateString()}</div>}
              </div>
              <div className="md:col-span-3 text-right space-x-3">
                <button
                  data-testid={`admin-coupon-toggle-${c.code}`}
                  onClick={() => toggleActive(c)}
                  className={`text-xs uppercase tracking-[0.15em] border px-3 py-2 ${c.active ? "border-[#E8A851]/40 text-[#E8A851]" : "border-white/20 text-zinc-500"}`}
                >
                  {c.active ? "Active" : "Disabled"}
                </button>
                <button
                  data-testid={`admin-coupon-delete-${c.code}`}
                  onClick={() => remove(c.code)}
                  className="text-xs text-red-400 hover:text-red-300 uppercase tracking-[0.15em]"
                >
                  <Trash2 className="w-3 h-3 inline" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------- Users Panel (Wholesale approvals) ---------- */
function UsersPanel({ headers }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/users`, { headers: headers() });
      setUsers(data);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const toggleWholesale = async (u) => {
    try {
      await axios.patch(
        `${API}/admin/users/${u.id}/wholesale`,
        { wholesale_approved: !u.wholesale_approved },
        { headers: headers() }
      );
      toast.success(!u.wholesale_approved ? "Wholesale approved" : "Wholesale revoked");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (loading) return <PanelLoading />;
  if (users.length === 0) return <EmptyPanel icon={Users} label="No customers yet." />;

  return (
    <div className="border border-white/10 bg-white/[0.02] divide-y divide-white/10">
      <div className="px-5 py-3 text-xs uppercase tracking-[0.15em] text-zinc-500 bg-white/[0.02]">
        Toggle wholesale to auto-apply tier pricing ($51/$50/$49) at checkout for orders of 10+ singles.
      </div>
      {users.map((u) => (
        <div key={u.id} data-testid={`admin-user-row-${u.id}`} className="p-5 grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
          <div className="md:col-span-4">
            <p className="font-display text-lg">{u.name}</p>
            <p className="text-sm text-zinc-400">{u.email}</p>
          </div>
          <div className="md:col-span-4 text-xs text-zinc-500">
            Joined {new Date(u.created_at).toLocaleDateString()}
          </div>
          <div className="md:col-span-4 text-right">
            <button
              data-testid={`admin-user-wholesale-toggle-${u.id}`}
              onClick={() => toggleWholesale(u)}
              className={`text-xs uppercase tracking-[0.15em] border px-4 py-2 ${
                u.wholesale_approved
                  ? "border-[#E8A851] bg-[#E8A851]/10 text-[#E8A851]"
                  : "border-white/20 text-zinc-400"
              }`}
            >
              {u.wholesale_approved ? "✓ Wholesale approved" : "Approve wholesale"}
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ---------- Affiliates Panel ---------- */
function AffiliatesPanel({ headers }) {
  const [affiliates, setAffiliates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null); // affiliate id being expanded (commissions)
  const [commissions, setCommissions] = useState({}); // { affiliate_id: [...] }
  const [savingCommission, setSavingCommission] = useState({});
  const [digestSending, setDigestSending] = useState(false);
  const [digestPreview, setDigestPreview] = useState(null);
  const [digestLoading, setDigestLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/affiliates`, { headers: headers() });
      setAffiliates(data);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const setStatus = async (a, status) => {
    try {
      await axios.patch(`${API}/admin/affiliates/${a.id}`, { status }, { headers: headers() });
      toast.success(`Marked ${status}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const setField = (id, field, value) =>
    setAffiliates((prev) => prev.map((a) => (a.id === id ? { ...a, [field]: value } : a)));

  const saveRates = async (a) => {
    try {
      const body = {
        tier_rate_1: parseFloat(a.tier_rate_1),
        tier_rate_2: parseFloat(a.tier_rate_2),
        tier_threshold: parseInt(a.tier_threshold, 10),
      };
      if (a.custom_rate === "" || a.custom_rate === null || a.custom_rate === undefined) {
        // leave custom_rate as-is via null-noop
      } else {
        body.custom_rate = parseFloat(a.custom_rate);
      }
      await axios.patch(`${API}/admin/affiliates/${a.id}`, body, { headers: headers() });
      toast.success("Rates saved");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleExpand = async (a) => {
    if (expanded === a.id) { setExpanded(null); return; }
    setExpanded(a.id);
    try {
      const { data } = await axios.get(`${API}/admin/affiliates/${a.id}/commissions`, { headers: headers() });
      setCommissions((prev) => ({ ...prev, [a.id]: data }));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const payCommission = async (aid, cid) => {
    setSavingCommission((s) => ({ ...s, [cid]: true }));
    try {
      await axios.patch(`${API}/admin/commissions/${cid}/pay`, { payout_note: "" }, { headers: headers() });
      toast.success("Commission marked paid");
      const { data } = await axios.get(`${API}/admin/affiliates/${aid}/commissions`, { headers: headers() });
      setCommissions((prev) => ({ ...prev, [aid]: data }));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setSavingCommission((s) => ({ ...s, [cid]: false }));
  };

  if (loading) return <PanelLoading />;
  if (affiliates.length === 0) return <EmptyPanel icon={Award} label="No affiliate applications yet." />;

  const loadDigest = async () => {
    setDigestLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/affiliates/digest/preview`, { headers: headers() });
      setDigestPreview(data);
    } catch (e) { toast.error(formatApiError(e)); }
    setDigestLoading(false);
  };

  const sendDigestNow = async () => {
    setDigestSending(true);
    try {
      const { data } = await axios.post(`${API}/admin/affiliates/digest/send`, {}, { headers: headers() });
      toast.success(`Digest sent — ${data.digest.top_affiliates.length} affiliate(s), $${data.digest.totals.commissions.toFixed(2)} in commissions`);
    } catch (e) { toast.error(formatApiError(e)); }
    setDigestSending(false);
  };

  return (
    <div className="space-y-4">
      {/* Weekly digest toolbar */}
      <div data-testid="admin-digest-toolbar" className="border border-white/10 bg-white/[0.02] p-5 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <p className="eyebrow mb-1">Weekly digest</p>
          <p className="text-xs text-zinc-500">
            Auto-sends every Monday 09:00 UTC to <span className="text-zinc-300">orders@stageletics.com</span>.
            {digestPreview && (
              <span className="text-zinc-300 ml-2">
                · {digestPreview.period_label}: {digestPreview.top_affiliates.length} affiliate(s), ${digestPreview.totals.commissions.toFixed(2)} commissions
              </span>
            )}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="admin-digest-preview-button"
            onClick={loadDigest}
            disabled={digestLoading}
            className="btn-secondary !border-white/20 disabled:opacity-50"
          >
            {digestLoading ? <><Loader2 className="w-4 h-4 animate-spin" /> Loading…</> : <>Preview</>}
          </button>
          <button
            data-testid="admin-digest-send-button"
            onClick={sendDigestNow}
            disabled={digestSending}
            className="btn-secondary !border-[#E8A851]/40 !text-[#E8A851] disabled:opacity-50"
          >
            {digestSending ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <>Send digest now</>}
          </button>
        </div>
      </div>

      {digestPreview && (
        <div data-testid="admin-digest-preview" className="border border-[#E8A851]/30 bg-[#E8A851]/[0.03] p-5">
          <p className="eyebrow mb-3">Digest preview · {digestPreview.period_label}</p>
          {digestPreview.top_affiliates.length === 0 ? (
            <p className="text-sm text-zinc-400">No affiliate activity this week.</p>
          ) : (
            <div className="divide-y divide-white/10">
              {digestPreview.top_affiliates.map((a, i) => (
                <div key={a.code} className="py-2 grid grid-cols-4 gap-2 text-sm">
                  <span className="text-white">#{i + 1} {a.name} <span className="text-zinc-500 font-mono text-xs">{a.code}</span></span>
                  <span className="text-right">{a.orders} orders</span>
                  <span className="text-right">${a.revenue.toFixed(2)}</span>
                  <span className="text-right text-[#E8A851]">+${a.commission.toFixed(2)}</span>
                </div>
              ))}
            </div>
          )}
          <div className="mt-3 pt-3 border-t border-white/10 flex justify-between text-xs text-zinc-500">
            <span>Total revenue: <span className="text-white">${digestPreview.totals.revenue.toFixed(2)}</span></span>
            <span>Total commissions: <span className="text-[#E8A851]">${digestPreview.totals.commissions.toFixed(2)}</span></span>
            <span>Clicks: <span className="text-white">{digestPreview.totals.clicks}</span></span>
          </div>
        </div>
      )}

      {affiliates.map((a) => (
        <div key={a.id} data-testid={`admin-affiliate-${a.id}`} className="border border-white/10 bg-white/[0.02]">
          {/* Row header */}
          <div className="p-5 grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
            <div className="md:col-span-3">
              <p className="font-display text-lg">{a.name}</p>
              <p className="text-xs text-zinc-500">{a.email}</p>
              <p className="font-mono text-[#E8A851] text-sm mt-2">{a.code}</p>
            </div>
            <div className="md:col-span-3 text-xs text-zinc-400">
              <p>Payment: <span className="text-white capitalize">{a.payment_method}</span></p>
              <p className="mt-1 break-all">{a.payment_details}</p>
            </div>
            <div className="md:col-span-3 text-xs text-zinc-400">
              <p>Clicks: <span className="text-white">{a.clicks || 0}</span></p>
              <p>Orders: <span className="text-white">{a.orders || 0}</span></p>
              <p>Unpaid: <span className="text-[#E8A851]">${Number(a.unpaid_balance || 0).toFixed(2)}</span></p>
              <p>Paid out: <span className="text-emerald-400">${Number(a.paid_out || 0).toFixed(2)}</span></p>
            </div>
            <div className="md:col-span-3 text-right space-y-2">
              <div>
                <span className={`inline-block text-[10px] uppercase tracking-[0.15em] rounded-full px-2 py-1 border ${
                  a.status === "active" ? "border-emerald-400/40 text-emerald-300" :
                  a.status === "pending" ? "border-[#E8A851]/40 text-[#E8A851]" :
                  "border-red-400/40 text-red-300"
                }`}>{a.status}</span>
              </div>
              <div className="flex gap-2 justify-end flex-wrap">
                {a.status === "pending" && (
                  <>
                    <button
                      data-testid={`admin-affiliate-approve-${a.id}`}
                      onClick={() => setStatus(a, "active")}
                      className="text-xs uppercase tracking-[0.15em] border border-emerald-400/40 text-emerald-300 px-3 py-1 hover:bg-emerald-400/10"
                    >Approve</button>
                    <button
                      data-testid={`admin-affiliate-reject-${a.id}`}
                      onClick={() => setStatus(a, "rejected")}
                      className="text-xs uppercase tracking-[0.15em] border border-red-400/40 text-red-300 px-3 py-1 hover:bg-red-400/10"
                    >Reject</button>
                  </>
                )}
                {a.status === "active" && (
                  <button
                    data-testid={`admin-affiliate-pause-${a.id}`}
                    onClick={() => setStatus(a, "rejected")}
                    className="text-xs uppercase tracking-[0.15em] border border-white/20 text-zinc-400 px-3 py-1"
                  >Pause</button>
                )}
                <button
                  data-testid={`admin-affiliate-expand-${a.id}`}
                  onClick={() => toggleExpand(a)}
                  className="text-xs uppercase tracking-[0.15em] border border-[#E8A851]/40 text-[#E8A851] px-3 py-1"
                >
                  {expanded === a.id ? "Hide" : "Details"}
                </button>
              </div>
            </div>
          </div>

          {expanded === a.id && (
            <div className="border-t border-white/10 p-6 space-y-6 bg-white/[0.01]">
              {/* Rates */}
              <div>
                <p className="eyebrow mb-3">Commission rates</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <RateInput testid={`rate1-${a.id}`} label="Tier 1 %" value={a.tier_rate_1}
                    onChange={(v) => setField(a.id, "tier_rate_1", v)} />
                  <RateInput testid={`rate2-${a.id}`} label="Tier 2 %" value={a.tier_rate_2}
                    onChange={(v) => setField(a.id, "tier_rate_2", v)} />
                  <RateInput testid={`threshold-${a.id}`} label="Tier threshold" value={a.tier_threshold}
                    onChange={(v) => setField(a.id, "tier_threshold", v)} step="1" />
                  <RateInput testid={`custom-${a.id}`} label="Custom % (overrides)"
                    value={a.custom_rate === null ? "" : a.custom_rate}
                    onChange={(v) => setField(a.id, "custom_rate", v)} placeholder="none" />
                </div>
                <button
                  data-testid={`admin-affiliate-save-rates-${a.id}`}
                  onClick={() => saveRates(a)}
                  className="btn-secondary mt-4 !border-[#E8A851]/40 !text-[#E8A851]"
                >
                  <Save className="w-4 h-4" /> Save rates
                </button>
              </div>

              {/* Commissions */}
              <div>
                <p className="eyebrow mb-3">Commissions</p>
                {!commissions[a.id] ? (
                  <div className="text-sm text-zinc-500 flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" /> Loading…
                  </div>
                ) : commissions[a.id].length === 0 ? (
                  <p className="text-sm text-zinc-500">No commissions yet.</p>
                ) : (
                  <div className="border border-white/10 divide-y divide-white/10">
                    {commissions[a.id].map((c) => (
                      <div key={c.id} data-testid={`admin-commission-${c.id}`} className="p-4 grid grid-cols-1 md:grid-cols-6 gap-3 items-center">
                        <div className="text-xs text-zinc-500 md:col-span-2">{new Date(c.created_at).toLocaleDateString()}</div>
                        <div className="text-sm">Sale #{c.sale_number} · {c.rate_used}%</div>
                        <div className="text-sm">Order: <span className="text-white">${Number(c.order_subtotal).toFixed(2)}</span></div>
                        <div className="font-display text-lg text-[#E8A851]">+${Number(c.commission_amount).toFixed(2)}</div>
                        <div className="text-right">
                          {c.status === "paid" ? (
                            <span className="text-[10px] uppercase tracking-[0.15em] text-emerald-400">
                              <CheckCircle2 className="w-3 h-3 inline mr-1" /> Paid
                            </span>
                          ) : (
                            <button
                              data-testid={`admin-commission-pay-${c.id}`}
                              onClick={() => payCommission(a.id, c.id)}
                              disabled={!!savingCommission[c.id]}
                              className="text-xs uppercase tracking-[0.15em] border border-emerald-400/40 text-emerald-300 px-3 py-1 hover:bg-emerald-400/10 disabled:opacity-50"
                            >
                              {savingCommission[c.id] ? <Loader2 className="w-3 h-3 animate-spin inline" /> : "Mark paid"}
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function RateInput({ label, value, onChange, testid, step = "0.1", placeholder }) {
  return (
    <div>
      <label className="eyebrow mb-2 block">{label}</label>
      <input
        data-testid={testid}
        type="number" step={step} min="0"
        value={value === null || value === undefined ? "" : value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-transparent border-b border-white/20 focus:border-[#E8A851] text-white px-0 py-2 focus:outline-none"
      />
    </div>
  );
}

/* ---------- helpers ---------- */
function PanelLoading() {
  return (
    <div className="p-12 text-zinc-500 flex items-center gap-2">
      <Loader2 className="w-4 h-4 animate-spin" /> Loading…
    </div>
  );
}
function EmptyPanel({ icon: Icon, label }) {
  return (
    <div className="border border-white/10 bg-white/[0.02] p-16 text-center">
      <Icon className="w-8 h-8 text-zinc-600 mx-auto mb-4" />
      <p className="text-zinc-500 text-sm">{label}</p>
    </div>
  );
}
